/**
 * Поиск по каталогу — один для панели каталога на чертеже и для каталога в 3D.
 *
 * Запрос сравнивается с названием модели и названием её типа (его видно на карточке):
 * сначала идут карточки, чьё название начинается с запроса, следом — те, где запрос
 * встречается внутри названия; второй список без тех, что уже есть в первом. Внутри
 * каждой группы порядок как в каталоге. Регистр, «ё» и лишние пробелы не важны.
 * Модели одного типа приходят одной карточкой, как и в самом каталоге: нашлась одна
 * модель типа — карточка под её именем, несколько — под названием типа.
 */
import { PLACEABLE_SECTION_KEYS, catalogSection, type CatalogSectionKey } from "./catalog";
import { catalogCards } from "./families";
import type { CatalogItemBase } from "./types";

/** Найденная карточка: раздел, элемент, который с неё ставится, и её название. */
export interface CatalogSearchHit {
	sectionKey: CatalogSectionKey;
	item: CatalogItemBase;
	title: string;
}

/** Короче запрос — слишком много случайных совпадений, поиск не начинается. */
export const CATALOG_SEARCH_MIN_LENGTH = 2;

/** Регистр, «ё» и повторные пробелы не важны: «Кровать  ДЁ» = «кровать де». */
const normalize = (text: string) => text.toLowerCase().replaceAll("ё", "е").replaceAll(/\s+/g, " ");

/** Насколько название подходит под запрос: с начала, где-то внутри или никак. */
enum Match {
	Starts = 0,
	Includes = 1,
	None = 2,
}

const matchOf = (title: string, query: string): Match => {
	const text = normalize(title);
	if (text.startsWith(query)) return Match.Starts;
	return text.includes(query) ? Match.Includes : Match.None;
};

/** Ищет по разделам `keys` (по умолчанию — по всем, что ставятся из каталога). */
export const searchCatalog = (
	query: string,
	keys: readonly CatalogSectionKey[] = PLACEABLE_SECTION_KEYS,
): CatalogSearchHit[] => {
	const trimmed = normalize(query.trim());
	if (trimmed.length < CATALOG_SEARCH_MIN_LENGTH) return [];
	const starts: CatalogSearchHit[] = [];
	const includes: CatalogSearchHit[] = [];
	for (const key of keys) {
		const section = catalogSection(key);
		const familyNames = new Map(section.families?.map((family) => [family.id, family.name]));
		// Лучшее совпадение модели — по её названию или по названию её типа
		const matchById = new Map<string, Match>();
		for (const item of section.items) {
			const familyName = item.family ? familyNames.get(item.family) : undefined;
			const match = Math.min(
				matchOf(item.name, trimmed),
				familyName ? matchOf(familyName, trimmed) : Match.None,
			);
			if (match !== Match.None) matchById.set(item.id, match);
		}
		const matched = section.items.filter((item) => matchById.has(item.id));
		// Карточка типа идёт в первую группу, если с начала совпала хоть одна его модель
		const familyMatch = new Map<string, Match>();
		for (const item of matched) {
			if (!item.family) continue;
			const match = matchById.get(item.id) ?? Match.None;
			familyMatch.set(item.family, Math.min(familyMatch.get(item.family) ?? Match.None, match));
		}
		for (const card of catalogCards(section, matched)) {
			const match = card.item.family
				? familyMatch.get(card.item.family)
				: matchById.get(card.item.id);
			const hit = {
				sectionKey: key,
				item: card.item,
				title: card.variants > 1 ? card.title : card.item.name,
			};
			(match === Match.Starts ? starts : includes).push(hit);
		}
	}
	return [...starts, ...includes];
};
